import React, { useEffect, useMemo, useState } from 'react';
import { Contract } from 'cpes-common';
import { Layout } from '@vwfs-bronson/bronson-react';
import { DocumentTypeSelect } from './DocumentTypeSelect';
import { TimeframeSelect } from './TimeframeSelect';
import { DocumentsTable } from './DocumentsTable';
import { useServerPaginatedDocuments } from './useServerPaginatedDocuments';
import { usePageResetOnChange } from './usePageResetOnChange';
import { ContractDocument } from './types';
import { PostboxDocumentType } from '../helper/DocumentType';
import { getDocumentTypeOptionsForContracts } from './contractDocumentTypes';
import { AllTimeframes, Timeframe, timeframeMomentMapping } from '../helper/Timeframe';

const contractDocumentTypeOptions: PostboxDocumentType[] = [
    'FINANCING_CONTRACT',
    'LONG_DRIVE_SERVICE_CONTRACT',
    'PARTICULAR_CONDITIONS',
    'CARDIF_INSURANCE_POLICY',
    'SEPA_MANDATE',
    'CONSUMER_CREDIT_INFORMATION',
    'WELCOME_LETTER',
    'MASTER_AGREEMENT',
];
const pageSize = 10;

export const ContractDocumentationTab: React.FC<{
    contracts: Contract[];
    contractNumber?: string;
    isActive?: boolean;
}> = ({ contracts, contractNumber, isActive = true }) => {
    const [page, setPage] = usePageResetOnChange(contractNumber);
    const [documentType, setDocumentType] = useState<PostboxDocumentType>('ALL');
    const [timeframe, setTimeframe] = useState<Timeframe | AllTimeframes>('ALL');

    const documentTypeOptions = useMemo(
        () =>
            contractNumber
                ? getDocumentTypeOptionsForContracts(contractDocumentTypeOptions, contracts, contractNumber)
                : [],
        [contracts, contractNumber],
    );
    const isDocumentTypeAvailable = documentType === 'ALL' || documentTypeOptions.includes(documentType);

    useEffect(() => {
        if (!isDocumentTypeAvailable) {
            setDocumentType('ALL');
        }
    }, [isDocumentTypeAvailable]);

    const documentTypeFilter = documentType === 'ALL' || !isDocumentTypeAvailable ? undefined : documentType;
    const fromDate = useMemo(() => (timeframe === 'ALL' ? undefined : timeframeMomentMapping()[timeframe]), [
        timeframe,
    ]);

    const { data, isLoading, loadingError } = useServerPaginatedDocuments<ContractDocument>(
        contractNumber && isActive ? '/contract-documents' : null,
        {
            page,
            pageSize,
            documentType: documentTypeFilter,
            contractNumber: contractNumber === 'ALL' ? undefined : contractNumber,
            fromDate,
        },
    );

    const resetToFirstPage = <T,>(setter: (value: T) => void) => (value: T) => {
        setter(value);
        setPage(1);
    };

    if (!contractNumber) {
        return null;
    }

    return (
        <>
            <Layout>
                <Layout.Item default={'1/2'} xs={'1/1'}>
                    <DocumentTypeSelect
                        options={documentTypeOptions}
                        value={isDocumentTypeAvailable ? documentType : 'ALL'}
                        onChange={resetToFirstPage(setDocumentType)}
                    />
                </Layout.Item>
                <Layout.Item default={'1/2'} xs={'1/1'}>
                    <TimeframeSelect value={timeframe} onChange={resetToFirstPage(setTimeframe)} />
                </Layout.Item>
            </Layout>
            <DocumentsTable
                documents={data?.documents}
                isLoading={isLoading}
                hasError={!!loadingError}
                currentPage={data?.currentPage ?? page}
                totalPages={data?.totalPages ?? 1}
                onPageChange={setPage}
                renderDetails={document =>
                    document.operationCode ? `(${document.operationCode})` : `(${document.fileName})`
                }
            />
        </>
    );
};
